<template>
  <div class="category-manage-container">
    <!-- 페이지 제목 섹션 -->
    <div class="container-wrapper">
      <div class="page-title-container">
        <div class="page-title">
          <h3 class="fw-bold" style="color: #000; font-size: 1.8rem">카테고리 관리</h3>
          <span class="pageDesc">인증서 및 병원에서 사용하는 카테고리를 등록하고 관리할 수 있습니다.</span>
        </div>
      </div>
    </div>

    <!-- 검색 필터 섹션 -->
    <div class="container-wrapper">
      <category-search-filter
        :initial-filters="filterData"
        @search="handleSearch"
        @reset="handleFilterReset"
        @filter-change="handleFilterChange"
      />
    </div>

    <!-- 목록 정보 및 카테고리 등록 버튼 -->
    <div class="container-wrapper">
      <div class="col-xs-12 table-info-filter">
        <div class="total-div">
          <p>전체 <span>{{ addCommaToNumber(listSize) }}</span>건</p>
        </div>
        <div class="search-container">
          <div class="select-wrapper">
            <select class="search-select" v-model="rowsPerPage" @change="handleRowsPerPageChange">
              <option :value="10">10개씩 보기</option>
              <option :value="20">20개씩 보기</option>
              <option :value="50">50개씩 보기</option>
            </select>
          </div>
          <button class="yellow-button add-category-btn" type="button" @click="openCreateModal">
            <i class="fa-solid fa-plus"></i>
            <span>카테고리 등록</span>
          </button>
        </div>
      </div>
      
      <!-- 드래그앤드롭 카테고리 리스트 -->
      <category-table
        :items="categoryList"
        :is-loading="isLoading"
        :current-page="currentPage"
        :rows-per-page="rowsPerPage"
        @update:items="handleItemsUpdate"
        @drag-end="handleDragEnd"
        @edit="openEditModal"
        @delete="confirmDelete"
      />
      
      <!-- 페이지네이션 -->
      <pagination
        :page-info="paginationConfig"
        page-name="CategoryManage"
        @change-page="paginationHandler"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { addCommaToNumber, twoBtnDialog } from "@/utils/utils";
import { MedicalDept } from "@/utils/enums";
import Pagination from "@/views/components/Pagination.vue";
import CategorySearchFilter from "./components/CategorySearchFilter.vue";
import CategoryTable from "./components/CategoryTable.vue";
import CategoryService from "./service/CategoryService";

export default defineComponent({
  name: "CategoryManage",
  components: {
    Pagination,
    CategorySearchFilter,
    CategoryTable
  },
  
  data() {
    return {
      // 카테고리 데이터
      categoryList: [],
      isLoading: false,
      
      // 페이지네이션 관련 데이터
      listSize: 0,
      rowsPerPage: 10,
      currentPage: 1,
      totalPages: 0,
      
      // 필터 데이터
      filterData: {
        department: MedicalDept.OPH, // 기본값: 안과
        useFilter: "",
        searchFilter: "name",
        condition: "",
      }
    };
  },
  
  computed: {
    /**
     * API 파라미터 구성
     */
    apiParams() {
      const params = {
        page: this.currentPage,
        limit: this.rowsPerPage,
        department: this.filterData.department,
        sortField: 'order',
        sortDirection: 'asc'
      };
      
      // 검색어 필터 추가
      if (this.filterData.condition && this.filterData.searchFilter === 'name') {
        params.name = this.filterData.condition;
      }
      
      // 사용 여부 필터 추가
      if (this.filterData.useFilter) {
        params.useFilter = this.filterData.useFilter;
      }
      
      return params;
    },
    
    /**
     * 페이지네이션 설정
     */
    paginationConfig() {
      return {
        currentPage: this.currentPage,
        totalPages: this.totalPages,
      };
    },
    
    /**
     * 카테고리 목록이 비어있는지 확인
     */
    isEmpty() {
      return this.categoryList.length === 0;
    }
  },
  
  mounted() {
    this.loadCategories();
  },
  
  methods: {
    addCommaToNumber,
    
    /**
     * 카테고리 목록 조회
     */
    async loadCategories() {
      if (this.isLoading) return;
      
      try {
        this.isLoading = true;
        
        // 서비스 레이어를 통한 API 호출
        const result = await CategoryService.getCategoryList(this.apiParams);
        
        // 응답 데이터 처리
        this.categoryList = result.items;
        this.listSize = result.total;
        this.totalPages = result.totalPages;
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * 필터 변경 이벤트 처리
     */
    handleFilterChange(filters) {
      this.filterData = { ...filters };
      this.loadCategories();
    },
    
    /**
     * 검색 이벤트 처리
     */
    handleSearch(filters) {
      this.filterData = { ...filters };
      this.currentPage = 1;
      this.loadCategories();
    },
    
    /**
     * 필터 초기화 이벤트 처리
     */
    handleFilterReset(filters) {
      this.filterData = { ...filters };
      this.currentPage = 1;
      this.loadCategories();
    },
    
    /**
     * 페이지 당 행 수 변경 이벤트 처리
     */
    handleRowsPerPageChange() {
      this.currentPage = 1;
      this.loadCategories();
    },
    
    /**
     * 항목 업데이트 이벤트 처리
     */
    handleItemsUpdate(items) {
      this.categoryList = [...items];
    },
    
    /**
     * 드래그앤드롭 이벤트 처리
     */
    async handleDragEnd(eventData) {
      try {
        this.isLoading = true;
        
        // 1. 모든 카테고리 데이터의 순서 정보 업데이트
        const reorderedItems = [...this.categoryList].map((item, index) => ({
          categoryId: item._id,
          order: index + 1
        }));
        
        // 2. 서비스 레이어를 통한 API 호출 - 모든 카테고리 순서 정보 전송
        const success = await CategoryService.updateCategoriesOrder(reorderedItems);
        
        // 3. 성공 시 목록 새로고침
        if (success) {
          this.$message.success('카테고리 순서가 변경되었습니다.');
          await this.loadCategories();
        } else {
          this.$message.error('카테고리 순서 변경 중 오류가 발생했습니다.');
          // 실패 시 원래 상태로 복원
          await this.loadCategories();
        }
      } catch (error) {
        console.error('카테고리 순서 변경 오류:', error);
        this.$message.error('카테고리 순서 변경 중 오류가 발생했습니다.');
        await this.loadCategories();
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * 페이지네이션 이벤트 처리
     */
    paginationHandler(childPassParam) {
      this.currentPage = childPassParam.page;
      this.loadCategories();
    },
    
    /**
     * 카테고리 등록 페이지로 이동
     */
    openCreateModal() {
      this.$router.push({ name: 'categoryCreate' });
    },
    
    /**
     * 카테고리 수정 페이지로 이동
     */
    openEditModal(category) {
      this.$router.push({ name: 'categoryEdit', params: { id: category._id } });
    },
    
    /**
     * 카테고리 삭제 확인 및 실행
     */
    async confirmDelete(category) {
      const isConfirmed = await twoBtnDialog(
        `카테고리 삭제`, 
        `"${category.name}" 카테고리를 삭제하시겠습니까?`, 
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      try {
        this.isLoading = true;
        
        // 서비스 레이어를 통한 API 호출
        const success = await CategoryService.deleteCategory(category._id);
        
        // 성공 시 목록 새로고침
        if (success) {
          this.$message.success(`"${category.name}" 카테고리가 삭제되었습니다.`);
          // 목록 새로고침 확실히 하기
          setTimeout(() => {
            this.loadCategories();
          }, 300);
        } else {
          this.$message.error('카테고리 삭제 중 오류가 발생했습니다.');
        }
      } catch (error) {
        console.error('카테고리 삭제 오류:', error);
        this.$message.error('카테고리 삭제 중 오류가 발생했습니다.');
      } finally {
        this.isLoading = false;
      }
    }
  }
});
</script>

<style lang="scss" scoped>
@import './styles/common.scss';

.category-manage-container {
  padding: 1vw;
  
  .search-container {
    display: flex;
    align-items: center;
    gap: $spacing-md;
    
    .select-wrapper {
      min-width: 130px;
    }
    
    .search-select {
      width: 100%;
      height: 38px;
      border: 1px solid $border-color-dark;
      border-radius: $border-radius-sm;
      padding: 0 $spacing-sm;
      background-color: $bg-white;
      cursor: pointer;
      
      &:focus {
        outline: none;
        border-color: $yellow-button-color;
      }
    }
    
    .add-category-btn {
      height: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 $spacing-md;
      
      i {
        margin-right: $spacing-xs;
      }
    }
  }
}
</style>
